import React, { useCallback } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import i18next from "i18next";
import _ from "lodash";
import AwesomeDebouncePromise from "awesome-debounce-promise";
import { Tab, Tabs } from "@mui/material";

import useStyles from "style/js-style/containers/administration/calculator/CalculatorDialogStyle";
import EATabPanel from "components/common/EATabPanel";
import {
  CalculationResultType,
  getVariableModel,
  VariableType
} from "services/common/CalculatorUtils";
import InnerVariables from './InnerVariables';


const Variables = ({
  fieldName,
  onVariableChangeCallBacks,
  removeDuplicateVariable,
  addDuplicateVariable,
  resultType,
  readOnly = false,
}) => {
  const { classes } = useStyles();
  const [selectedTab, setSelectedTab] = React.useState(0);

  const formMethods = useFormContext();
  const {
    control,
    setError,
    clearErrors,
    getValues,
  } = formMethods;

  const { fields: variables, append, remove } = useFieldArray({
    control,
    name: `${fieldName}`,
  });

  //TODO: re-work callbacks triggering ?
  const callCallBacks = useCallback(async (currentVariable, action, value) => {
    for (const callback of Object.values(onVariableChangeCallBacks)) {
      if (typeof callback === 'function') {
        await callback(currentVariable, action, value);
      }
    }
  }, [onVariableChangeCallBacks]);

  // Add a new variable
  const onAddEntry = useCallback((type) => {
    append(getVariableModel(type));
  }, [append]);

  const getNthEntry = useCallback((index) => {
    return _.nth(variables, index);
  }, [variables]);

  // Delete a variable
  const onRemoveEntry = useCallback((index) => {
    //TODO: re-work callbacks triggering and dependent criteria deletion
    const entryDelete = getNthEntry(index);
    callCallBacks(entryDelete, "delete");

    clearErrors(`${fieldName}.${index}`);
    remove(index);
  }, [fieldName, remove, clearErrors, getNthEntry, callCallBacks]);

  const validateName = AwesomeDebouncePromise(async (value, fieldName, variableType) => {
    const CALCULATION_VARIABLE_NAME_MAX_LENGTH = 100;
    const TODAY_VARIABLE_NAME = "TODAY";

    let isValid = false;
    let typeErrMsg = "";
    const rootFieldName = fieldName.slice(0, fieldName.indexOf('.name'));
    const variableId = getValues(rootFieldName).id;

    if (value.length === 0) {
      typeErrMsg = i18next.t("infos.required");
    } else if (value.length > CALCULATION_VARIABLE_NAME_MAX_LENGTH) {
      typeErrMsg = i18next.t(
        "calculator.errors.variableNameMaxLengthExceeded",
        {
          length: CALCULATION_VARIABLE_NAME_MAX_LENGTH,
        }
      );
    } else {
      let bContinue = true;

      if (VariableType.RESULT_FORMULA_ARGUMENT === variableType) {
        if (!/^[a-zA-Z_$][a-zA-Z0-9_$]*$/.test(value)) {
          typeErrMsg = i18next.t(
            "calculator.errors.invalidVariableName",
            {
              length: CALCULATION_VARIABLE_NAME_MAX_LENGTH,
            }
          );
          bContinue = false;
        } else if (TODAY_VARIABLE_NAME === value.toUpperCase()) {
          typeErrMsg = i18next.t("calculator.errors.invalidVariableNameToday");
          bContinue = false;
        }
      }

      if (bContinue) {
        let isNameUnique = _.size(_.filter(getValues("variables"), { name: value })) <= 1;

        if (!isNameUnique) {
          addDuplicateVariable(variableId);
          typeErrMsg = i18next.t("infos.alreadyExistVariable");
        } else {
          removeDuplicateVariable(variableId);
          isValid = true;
        }
      }
    }

    if (isValid) {
      clearErrors(fieldName);
    } else {
      setError(fieldName, { type: "manual", message: typeErrMsg });
      return typeErrMsg;
    }
  }, 500);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const a11yProps = (index) => {
    return {
      id: `simple-tab-${index}`,
      "aria-controls": `simple-tabpanel-${index}`,
    };
  }

  return (
    <div className={classes.declarationVariables}>
      <Tabs
        value={selectedTab}
        onChange={handleTabChange}
        aria-label="variables tabs"
        sx={{ borderBottom: 1, borderColor: '#D4D4D4', marginLeft: "24px", }}
        variant="fullWidth"
      >
        <Tab
          label={i18next.t("calculator.dialog.create.label.criterionArguments")}
          {...a11yProps(0)}
          className={classes.tab}
        />
        {resultType === CalculationResultType.ARITHMETIC_FORMULA_OUTPUT &&
          <Tab
            label={i18next.t("calculator.dialog.create.label.resultFormulaArguments")}
            {...a11yProps(1)}
            className={classes.tab}
            disabled={CalculationResultType.ARITHMETIC_FORMULA_OUTPUT !== resultType}
          />
      }
      </Tabs>

      <EATabPanel value={selectedTab} index={0} variant={"withoutBackground"} style={{ flex: 1, padding: "24px", }}>
        <InnerVariables
          fieldName={`${fieldName}`}
          items={variables}
          type={VariableType.CRITERION_ARGUMENT}
          onAddEntry={onAddEntry}
          onRemoveEntry={onRemoveEntry}
          validateName={validateName}
          onVariableChangeCallBack={callCallBacks}
          readOnly={readOnly}
        />
      </EATabPanel>
      {resultType === CalculationResultType.ARITHMETIC_FORMULA_OUTPUT &&
        <EATabPanel value={selectedTab} index={1} variant={"withoutBackground"} style={{ flex: 1, padding: "24px", }}>
          <InnerVariables
            fieldName={`${fieldName}`}
            items={variables}
            type={VariableType.RESULT_FORMULA_ARGUMENT}
            onAddEntry={onAddEntry}
            onRemoveEntry={onRemoveEntry}
            validateName={validateName}
            onVariableChangeCallBack={callCallBacks}
            readOnly={readOnly}
          />
        </EATabPanel>
      }
    </div>
  )
}

export default Variables;
